import { createTheme, ThemeOptions } from '@mui/material/styles';
import {
  orange,
  grey,
  amber,
  yellow,
  red,
  purple,
  blue,
  green,
} from '@mui/material/colors';
import deepmerge from '@mui/utils/deepmerge';
import { ENVIRONMENT } from '@/constants/environment';
import { FixtureAction } from '@/service/types';
import { MATCH_ACTION_SEND_TYPE } from '@/service/constants';
import { THEME_MODE } from './constants';

export type ActionSendTypeColor = Record<FixtureAction['sendTypeId'], string>;
export type ActionSendTypeColorOptions = Partial<ActionSendTypeColor>;

declare module '@mui/material/styles' {
  interface Palette {
    env: Partial<Record<ENVIRONMENT, string>>;
    json: {
      main: string;
      blue: string;
      red: string;
      purple: string;
      background: string;
    };
    status: ActionSendTypeColor;
    tableBorderColor: string;
    tableHeaderColor: string;
    iconsColors: {
      borderColor: string;
      yellowCard: string;
      redCard: string;
    };
    defaultSoundButton: string;
    snackbarColors: {
      possibleVar: { backgroundColor: string };
      flagOnField: { backgroundColor: string };
    };
    timeoutColor: string;
  }

  interface Theme {
    palette: Palette;
  }

  interface PaletteOptions {
    env?: Partial<Record<ENVIRONMENT, string>>;
    json?: {
      main?: string;
      blue?: string;
      red?: string;
      purple?: string;
      background?: string;
    };
    status?: ActionSendTypeColorOptions;
    tableBorderColor?: string;
    tableHeaderColor?: string;
    iconsColors?: {
      borderColor: string;
      yellowCard: string;
      redCard: string;
    };
    defaultSoundButton?: string;
    timeoutColor?: string;
    snackbarColors?: {
      possibleVar: { backgroundColor: string };
      flagOnField: { backgroundColor: string };
    };
  }
  interface ThemeOptions {
    palette?: PaletteOptions;
  }
}

export const lightOnlyOptions: ThemeOptions = {
  palette: {
    mode: THEME_MODE.LIGHT,
    background: {
      default: grey[100],
    },
    json: {
      main: grey[700],
      blue: '#2196F3',
      red: '#D32F2F',
      purple: '#6A1B9A',
      background: grey[100],
    },
    status: {
      [MATCH_ACTION_SEND_TYPE.PENDING]: purple[600],
      [MATCH_ACTION_SEND_TYPE.UPDATED]: blue[600],
      [MATCH_ACTION_SEND_TYPE.CANCELLED]: amber[800],
      [MATCH_ACTION_SEND_TYPE.DELETED]: red[800],
      [MATCH_ACTION_SEND_TYPE.CONFIRMED]: green[700],
    },
    tableBorderColor: grey[300],
    tableHeaderColor: grey[100],
    timeoutColor: grey[200],
  },
  typography: {
    caption: {
      color: grey[700],
    },
  },
};

export const darkOnlyOptions: ThemeOptions = {
  palette: {
    mode: THEME_MODE.DARK,
    json: {
      main: grey[400],
      blue: '#42A5F5',
      red: '#E53935',
      purple: '#BA68C8',
      background: grey[900],
    },
    status: {
      [MATCH_ACTION_SEND_TYPE.PENDING]: purple[200],
      [MATCH_ACTION_SEND_TYPE.UPDATED]: blue[300],
      [MATCH_ACTION_SEND_TYPE.CANCELLED]: amber[300],
      [MATCH_ACTION_SEND_TYPE.DELETED]: red['A200'],
      [MATCH_ACTION_SEND_TYPE.CONFIRMED]: green[300],
    },
    tableBorderColor: grey[800],
    tableHeaderColor: grey[700],
    timeoutColor: grey[800],
  },
  typography: {
    caption: {
      color: grey[400],
    },
  },
};

export const commonOptions = {
  components: {
    MuiContainer: {
      styleOverrides: {
        root: ({ theme }) => ({
          paddingLeft: theme.spacing(1),
          paddingRight: theme.spacing(1),
        }),
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: grey[900],
          color: grey[100],
          backgroundImage: 'unset',
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: orange[900],
          color: grey[100],
        },
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        grouped: {
          '&.Mui-selected': {
            border: '1px solid',
          },
        },
      },
    },
  },
  shape: { borderRadius: 5 },
  palette: {
    primary: {
      main: '#FF4D00',
      contrastText: grey[100],
    },
    secondary: {
      light: grey[700],
      main: grey[800],
      dark: grey[900],
      contrastText: grey[50],
    },
    error: {
      light: red[400],
      dark: red[900],
      main: red[600],
      contrastText: grey[50],
    },
    env: {
      [ENVIRONMENT.DEV]: '#FFD900',
      [ENVIRONMENT.SANDBOX]: '#41adbf',
      [ENVIRONMENT.STAGING]: '#83ED6B',
    },
    iconsColors: {
      borderColor: grey[400],
      yellowCard: '#FFCD00',
      redCard: '#DF0000',
    },
    warning: {
      main: amber[900],
      light: yellow['600'],
    },
    defaultSoundButton: grey[600],
    snackbarColors: {
      possibleVar: { backgroundColor: '#9354CC' },
      flagOnField: { backgroundColor: yellow[700] },
    },
  },
  transitions: {
    duration: {
      enteringScreen: 175,
      leavingScreen: 145,
    },
  },
  typography: {
    fontFamily: '"SF-UI", Arial',
    caption: {
      fontWeight: 600,
    },
    overline: {
      fontWeight: 600,
      letterSpacing: '0.0833em',
    },
    subtitle2: {
      lineHeight: 1.4,
    },
  },
} as const satisfies ThemeOptions;

export const lightOptions = deepmerge(commonOptions, lightOnlyOptions);
export const darkOptions = deepmerge(commonOptions, darkOnlyOptions);

export const lightTheme = createTheme(lightOptions);
export const darkTheme = createTheme(darkOptions);

// @ts-ignore:next-line
window.theme = {
  light: lightTheme,
  dark: darkTheme,
};
